import isEmpty from 'lodash/isEmpty';
import {
  extractNonHistoricalTicketIdFromState,
  extractSelectedTicketIdFromState,
  getSimplrAccountIdOfSelectedTicket,
  getBrandOfSelectedTicket,
} from '../../extractFromState';
import * as ordersAPI from 'api/orders';
import { setLoading } from '../index';
import {
  setOrders,
  setOrderShippingAddress,
  setRefundAmount,
  setRefundAmountFormatted,
  setShowConfirmationPage,
} from './OrdersSearchActions';
import { getSelectedOrder, getSelectedOrderId, getSelectedShop } from '../../selectors/tools';
import { showError } from 'util/errorPopupUtil';
import pickBy from 'lodash/pickBy';
import { CANCEL_ACTIONS, CANCEL_TYPES, RETURN_TYPES } from '../../constants/decisionTree';
import * as ErrorService from '../../services/ErrorService/ErrorService';
import { priceFormat } from '../../util';

// PLEASE NOTE: This file is for the new common order interface with common data structure responses
// Do not use OrderSearchActions or CommonOrderActions for common interface actions

/**
 * Get high level order info
 * @param {object} options
 * @param {string} options.orderNumber
 * @param {string} options.customerEmail
 * @param {number} options.limit
 */
export const searchOrders = (options) => async (dispatch, getState) => {
  const { limit = 100 } = options;
  const state = getState();
  const conversationId = extractNonHistoricalTicketIdFromState(state);
  const brand = getBrandOfSelectedTicket(state);

  const params = {
    ...options, // spread because dynamic inputs (we don't know the param name in advance)
    limit,
    simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
    conversationId,
    brand,
  };

  try {
    dispatch(setLoading(true, 'searchPage'));
    const data = await ordersAPI.searchOrders(params);

    dispatch(setOrders(data.orders));
  } catch (error) {
    showError(error);
  } finally {
    dispatch(setLoading(false, 'searchPage'));
  }
};

export const fetchCommonOrder = async (simplrAccountId, order, conversationId, brand) => {
  try {
    const params = {
      simplrAccountId,
      orderId: order?.orderId,
      orderNumber: order?.orderNumber,
      conversationId,
      customerEmail: order?.customerEmail,
      domains: order?.simplrParams?.domain,
      brandName: order?.simplrParams?.brandName,
      brand,
    };

    // pickBy used for cutting empty params
    const result = await ordersAPI.getOrder(pickBy(params));

    return result.order;
  } catch (error) {
    showError(error);
  }
};

/**
 * this function is used only for printify, and uses the cancel route, which is not RESTful
 * @param {string} simplrAccountId
 * @param {string | number} orderId
 * @param {Array} items
 * @param {Object} reason
 * @param {number} reason.id
 * @param {string} reason.label
 * @param {Array} imageUrls
 * @param {string} domain
 * @returns
 */
export const replaceOrderItemsViaCancelRoute =
  (simplrAccountId, orderId, items, reason = null, imageUrls = null, domain) =>
  async (dispatch, getState) => {
    if (isEmpty(items)) {
      return;
    }

    try {
      dispatch(setLoading(true, 'orderCancelContainer'));

      const state = getState();
      const conversationId = extractNonHistoricalTicketIdFromState(state);
      const order = getSelectedOrder(state);
      const accountType = order?.extraParams?.vendorType || null;

      const orderItems = items.map((item) => ({
        itemId: String(item.itemId),
        itemQuantity: item.itemQuantity || 1,
        imageUrls,
        ...(item?.itemNumber && { itemNumber: item.itemNumber }),
      }));

      const params = {
        cancelAction: CANCEL_ACTIONS.REPLACE,
        cancelType: CANCEL_TYPES.FULL,
        simplrAccountId,
        domain,
        brandName: order?.simplrParams?.brandName,
        orderId: String(orderId),
        orderItems,
        ...(reason?.id && { reasonId: reason.id }),
        conversationId,
        ...(order?.customerEmail && { customerEmail: order.customerEmail }),
        ...(accountType && { accountType }),
      };

      await ordersAPI.cancelOrder(params);
      dispatch(setShowConfirmationPage());
    } catch (error) {
      showError(error);
    } finally {
      dispatch(setLoading(false, 'orderCancelContainer'));
    }
  };

export const cancelAndRefundOrderItems =
  ({ simplrAccountId, orderId, items, reason = null, domain, additionalOrderParams = {} }) =>
  async (dispatch, getState) => {
    const state = getState();
    const conversationId = extractNonHistoricalTicketIdFromState(state);
    const order = getSelectedOrder(state);
    const accountType = order?.extraParams?.vendorType || null;

    const orderItems = items.map((item) => ({
      itemId: String(item.itemId),
      itemQuantity: item.itemQuantity || 1,
      ...(item?.itemNumber && { itemNumber: item.itemNumber }),
    }));

    const params = {
      cancelAction: CANCEL_ACTIONS.REFUND,
      cancelType: CANCEL_TYPES.FULL,
      simplrAccountId,
      domain,
      brandName: order?.simplrParams?.brandName,
      orderId: String(orderId),
      orderItems,
      conversationId,
      ...(reason?.id && { reasonId: reason.id }),
      ...(reason?.label && { reason: reason.label }),
      ...(order?.customerEmail && { customerEmail: order.customerEmail }),
      ...(accountType && { accountType }),
      ...additionalOrderParams,
    };

    const { refundAmount, refundCurrency } = await ordersAPI.cancelOrder(params);

    dispatch(setRefundAmount(refundAmount));
    dispatch(setRefundAmountFormatted(priceFormat(refundAmount, refundCurrency)));
  };

export const cancelOrderItems =
  (simplrAccountId, orderId, items, reason = null) =>
  async (dispatch, getState) => {
    const state = getState();
    const conversationId = extractNonHistoricalTicketIdFromState(state);
    const order = getSelectedOrder(state);
    const accountType = order?.extraParams?.vendorType || null;

    const orderItems = items.map((item) => ({
      itemId: item.itemId,
      itemQuantity: item.itemQuantity || 1,
      ...(item?.itemNumber && { itemNumber: item.itemNumber }),
    }));

    const params = {
      cancelAction: CANCEL_ACTIONS.CANCEL,
      cancelType: CANCEL_TYPES.FULL,
      simplrAccountId,
      orderId,
      orderItems,
      conversationId,
      ...(reason?.id && { reasonId: reason.id }),
      ...(reason?.label && { reason: reason.label }),
      ...(order?.customerEmail && { customerEmail: order.customerEmail }),
      ...(accountType && { accountType }),
    };

    await ordersAPI.cancelOrder(params);
  };

export const refundOrderItems =
  ({
    simplrAccountId,
    orderId,
    orderNumber,
    items,
    reason = null,
    imageUrls = [],
    domain,
    additionalOrderParams = {},
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const conversationId = extractNonHistoricalTicketIdFromState(state);
    const order = getSelectedOrder(state);
    const customerEmail = order?.customerEmail || null;
    const accountType = order?.extraParams?.vendorType || null;

    const orderItems = items.map((item) => ({
      orderItemId: String(item.itemId),
      itemQuantity: item.itemQuantity || 1,
      refundType: RETURN_TYPES.FULL,
      imageUrls,
      ...(item?.itemNumber && { itemNumber: item.itemNumber }),
    }));

    const params = {
      simplrAccountId,
      orderId: String(orderId),
      orderNumber: String(orderNumber),
      refundMap: orderItems,
      domain,
      conversationId,
      ...(reason?.id && { reasonId: reason.id }),
      ...(reason?.label && { reason: reason.label }),
      ...(customerEmail && { customerEmail }),
      ...(accountType && { accountType }),
      ...additionalOrderParams,
    };

    const { refundAmount, refundCurrency } = await ordersAPI.refundOrder(params);

    dispatch(setRefundAmount(refundAmount));
    dispatch(setRefundAmountFormatted(priceFormat(refundAmount, refundCurrency)));
  };

export const discountOrderItems =
  ({ simplrAccountId, orderId, items }) =>
  async (dispatch, getState) => {
    const state = getState();
    const conversationId = extractNonHistoricalTicketIdFromState(state);
    const shopName = getSelectedShop(state);
    const order = getSelectedOrder(state);
    const customerEmail = order?.customerEmail || null;
    const accountType = order?.extraParams?.vendorType || null;

    const orderItems = items.map((item) => ({
      itemId: String(item.itemId),
    }));

    const params = {
      simplrAccountId,
      conversationId,
      orderId: String(orderId),
      orderItems,
      shopName,
      ...(customerEmail && { customerEmail }),
      ...(accountType && { accountType }),
    };

    await ordersAPI.issueDiscount(params);
  };

export const replaceOrderItems =
  ({
    simplrAccountId,
    orderId,
    orderNumber,
    reason = null,
    chargeShipping,
    items,
    imageUrls = [],
    shippingAddress = null,
    domain,
    customerEmail,
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const conversationId = extractNonHistoricalTicketIdFromState(state);

    const params = {
      simplrAccountId,
      conversationId,
      orderId: String(orderId),
      orderNumber: String(orderNumber),
      ...(reason?.id && { reasonId: reason.id }),
      ...(reason?.label && { reason: reason.label }),
      chargeShipping,
      items: items.map((item) => ({
        itemId: String(item.itemId),
        itemQuantity: String(item.itemQuantity),
        imageUrls,
      })),
      shippingAddress,
      domain,
      customerEmail,
    };

    // throws error if fails, caught by the upper calling function
    await ordersAPI.issueReplacement(params);
  };

export const changeOrderAddress = (formData) => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true, 'itemTransactionContainer'));

    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const orderId = String(getSelectedOrderId(state));
    const shopName = getSelectedShop(state);
    const conversationId = extractSelectedTicketIdFromState(state);

    const params = {
      simplrAccountId,
      conversationId,
      orderId,
      shopName,
      shippingAddress: formData,
    };

    await ordersAPI.updateOrderAddress(params);

    dispatch(setOrderShippingAddress(formData));
    dispatch(setShowConfirmationPage());
  } catch (error) {
    ErrorService.handleError({
      error,
      customMessage: error.response?.text || error.data?.message,
      showPopup: true,
    });
  } finally {
    dispatch(setLoading(false, 'itemTransactionContainer'));
  }
};
